import * as React from 'react';
import { TDispatch, withProvider } from '../../redux';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger/shared';
import { IConfig } from '@cian/config/shared';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import { ApplicationContext } from '../ApplicationContext';
import { CommunicationType } from './CommunicationType';
import { useDispatch, useSelector } from 'react-redux';
import { selectPreferredCommunicationType } from '../../selectors/offer/selectPreferredCommunicationType';
import { changePreferredCommunicationType, EPreferredCommunicationType, sendPollAnswer } from '../../redux/reducers';
import { selectAnalyticsOfferStatus } from '../../selectors/analytics';
import { DisableFormsPollModal } from './DisableFormsPollModal';
import { trackCommunicationTypeShow, trackCommunicationTypeToggle } from './tracking';

interface ICommunicationTypeContainerProps {
  httpApi: IHttpApi;
  logger: ILogger;
  config: IConfig;
  telemetry: ITelemetryLogger;
  changeCommunicationType(communicationType: EPreferredCommunicationType): void;
  getCommunicationType(): EPreferredCommunicationType;
}

export const Container: React.FC<ICommunicationTypeContainerProps> = ({
  httpApi,
  logger,
  config,
  telemetry,
  changeCommunicationType,
  getCommunicationType,
}) => {
  const context = React.useMemo(() => ({ httpApi, logger, config, telemetry }), [httpApi, logger, config, telemetry]);

  const [open, setIsOpen] = React.useState(false);

  const isFormsEnabled =
    useSelector(selectPreferredCommunicationType) === EPreferredCommunicationType.Forms ||
    getCommunicationType() === EPreferredCommunicationType.Forms;

  const analyticsOfferStatus = useSelector(selectAnalyticsOfferStatus);

  const closePoll = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const dispatch = useDispatch<TDispatch>();

  const handleCommunicationTypeChange = React.useCallback(
    (formsChecked: boolean) => {
      if (formsChecked) {
        dispatch(changePreferredCommunicationType(EPreferredCommunicationType.Forms));
        changeCommunicationType(EPreferredCommunicationType.Forms);
      } else {
        dispatch(changePreferredCommunicationType(EPreferredCommunicationType.Phone));
        changeCommunicationType(EPreferredCommunicationType.Phone);
        if (analyticsOfferStatus === 'Edit_ad') {
          setIsOpen(true);
        }
      }
      trackCommunicationTypeToggle(formsChecked);
    },
    [dispatch, changeCommunicationType, analyticsOfferStatus],
  );

  const handleSendPollAnswer = React.useCallback(
    (answers: string[]) => {
      dispatch(sendPollAnswer(answers));
      closePoll();
    },
    [dispatch, closePoll],
  );

  React.useEffect(trackCommunicationTypeShow, []);

  return (
    <ApplicationContext.Provider value={context}>
      <CommunicationType checked={isFormsEnabled} onChange={handleCommunicationTypeChange} />
      <DisableFormsPollModal isOpen={open} close={closePoll} disableForms={handleSendPollAnswer} />
    </ApplicationContext.Provider>
  );
};

export const CommunicationTypeContainer = withProvider(Container);
